import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  CircularProgress,
  TextField,
  Alert,
  Snackbar,
  Skeleton,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import MicIcon from '@mui/icons-material/Mic';
import AddCommentIcon from '@mui/icons-material/AddComment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../hooks/reduxHooks';
import { selectReportingPrefs } from '../store/userPreferencesSlice';
import {
  updateReport,
  finalizeReport,
  regenerateReport,
  setEditing,
  setReport,
  selectIsReportEditable,
} from '../store/reportSlice';
import transcriptionService from '../services/transcriptionService';
import { loadSessionForAppend } from '../store/transcriptionSlice';
import ReportStatusBadge from '../components/ReportStatusBadge';
import PatientInfoDisplay from '../components/PatientInfoDisplay';
import VertebralNumberingPanel from '../components/VertebralNumberingPanel';
import HighlightedLevelText from '../components/HighlightedLevelText';
import { exportReportPDF, printReport, downloadReportText, type PatientContext } from '../utils/reportExport';
import { copyReportToClipboard, parseReportSubsections, type SubsectionItem } from '../utils/reportUtils';
import patientService, { PatientHistorySummaryEntry } from '../services/patientService';
import reportService, { type VertebralNumberingIssue } from '../services/reportService';
import addendumService from '../services/addendumService';
import CodeIcon from '@mui/icons-material/Code';
import ArticleIcon from '@mui/icons-material/Article';
import HistoryIcon from '@mui/icons-material/History';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import doctorService from '../services/doctorService';

// ═══════════════════════════════════════════════════════════════════
// Section parsing (shared logic)
// ═══════════════════════════════════════════════════════════════════

interface ReportSection {
  title: string;
  content: string;
}

// Known top-level report section headers (case-insensitive match).
// Subsection headers inside FINDINGS (e.g. "Bones:", "ACL:") are NOT
// top-level and must be kept as content within their parent section.
const TOP_LEVEL_HEADERS = new Set([
  'exam',
  'clinical data',
  'clinical history',
  'clinical indication',
  'technique',
  'comparison',
  'findings',
  'impression',
  'recommendation',
  'recommendations',
  'indication',
]);

function parseReportSections(reportText: string): ReportSection[] {
  const lines = reportText.split('\n');
  const sections: ReportSection[] = [];
  let currentTitle = '';
  let currentContent: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();

    // Check if the line starts with a known header followed by ':'
    // This handles both standalone headers ("FINDINGS:") and
    // inline-content headers ("EXAM: MRI Right Hip").
    let matchedHeader = '';
    let inlineContent = '';
    const colonIdx = trimmed.indexOf(':');
    if (colonIdx >= 3) {
      const candidate = trimmed.slice(0, colonIdx).trim().toLowerCase();
      if (TOP_LEVEL_HEADERS.has(candidate)) {
        matchedHeader = trimmed.slice(0, colonIdx + 1); // e.g. "EXAM:"
        inlineContent = trimmed.slice(colonIdx + 1).trim();
      }
    }

    if (matchedHeader) {
      if (currentTitle) {
        sections.push({ title: currentTitle, content: currentContent.join('\n').trim() });
      }
      currentTitle = matchedHeader;
      currentContent = inlineContent ? [inlineContent] : [];
    } else {
      currentContent.push(line);
    }
  }

  if (currentTitle) {
    sections.push({ title: currentTitle, content: currentContent.join('\n').trim() });
  }

  if (sections.length === 0 && reportText.trim()) {
    sections.push({ title: 'Report', content: reportText.trim() });
  }

  return sections;
}

// ═══════════════════════════════════════════════════════════════════
// Section Card Component
// ═══════════════════════════════════════════════════════════════════

interface SectionCardProps {
  section: ReportSection;
  canEdit: boolean;
  isEditing: boolean;
  isAnyEditing: boolean;
  isSaving: boolean;
  isImpression: boolean;
  status: string;
  loading: boolean;
  boldPositives: boolean;
  editValue: string;
  numberingIssues: VertebralNumberingIssue[];
  onStartEdit: (title: string, content: string) => void;
  onCancelEdit: () => void;
  onSaveSection: () => void;
  onEditChange: (title: string, value: string) => void;
  onAcceptImpression: () => void;
  onRegenerateImpression: () => void;
  onManualEditImpression: (content: string) => void;
  sectionRef: (el: HTMLElement | null) => void;
}

const SectionCard: React.FC<SectionCardProps> = ({
  section,
  canEdit,
  isEditing,
  isAnyEditing,
  isSaving,
  isImpression,
  status,
  loading,
  boldPositives,
  editValue,
  numberingIssues,
  onStartEdit,
  onCancelEdit,
  onSaveSection,
  onEditChange,
  onAcceptImpression,
  onRegenerateImpression,
  onManualEditImpression,
  sectionRef,
}) => {
  // Parse subsection structure using the unified frontend parser
  const [parsedItems, setParsedItems] = useState<SubsectionItem[]>([]);

  useEffect(() => {
    // Only parse FINDINGS and IMPRESSION sections for subsection formatting
    const sectionName = section.title.replace(/:$/, '').trim().toUpperCase();
    const shouldFormat = sectionName === 'FINDINGS' || sectionName === 'IMPRESSION';

    if (!shouldFormat) {
      setParsedItems([]);
      return;
    }

    setParsedItems(parseReportSubsections(section.content));
  }, [section.content, section.title]);

  return (
    <Paper
      ref={sectionRef}
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        mb: 2,
        border: '1px solid',
        borderColor: isEditing ? 'primary.main' : 'divider',
        borderRadius: 2,
        transition: 'border-color 0.2s ease',
        '&:hover': {
          borderColor: canEdit && !isAnyEditing ? 'primary.light' : undefined,
        },
      }}
    >
      {/* Section Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          color="primary.main"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.875rem' }}
        >
          {section.title}
        </Typography>
        {canEdit && !isEditing && !isAnyEditing && (
          <Tooltip title="Edit this section">
            <IconButton
              size="small"
              onClick={() => onStartEdit(section.title, section.content)}
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Content */}
      {isEditing ? (
        <Box>
          <TextField
            multiline
            fullWidth
            minRows={4}
            value={editValue}
            onChange={(e) => onEditChange(section.title, e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.9375rem',
                lineHeight: 1.7,
                fontFamily: '"Inter", "Roboto", sans-serif',
              },
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
              onClick={onSaveSection}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Section'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CancelIcon />}
              onClick={onCancelEdit}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      ) : (
        // For FINDINGS/IMPRESSION, render with bold subsection headers
        parsedItems.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {(() => {
              const sectionName = section.title.replace(/:$/, '').trim().toUpperCase();
              const isFindings = sectionName === 'FINDINGS';
              let inPositiveZone = false;
              return parsedItems.map((item, itemIdx) => {
                if (item.type === 'subsection' && item.header) {
                  const headerUpper = item.header.replace(/:$/, '').trim().toUpperCase();
                  const isPositiveHeader = headerUpper === 'KEY ABNORMALITIES' || headerUpper === 'KEY CHANGES';
                  if (isFindings && isPositiveHeader) {
                    inPositiveZone = true;
                  } else if (isFindings) {
                    inPositiveZone = false;
                  }
                }
                const isPositiveText =
                  boldPositives && isFindings && inPositiveZone && item.type === 'text' && item.text.trim().length > 0;
                return (
                  <Box key={itemIdx}>
                    {item.type === 'subsection' ? (
                      <Box sx={{ mb: 0.5 }}>
                        <Typography
                          component="span"
                          fontWeight="bold"
                          sx={{
                            fontSize: '0.9375rem',
                            color: 'text.primary',
                            display: 'block',
                            mb: 0.25,
                          }}
                        >
                          {item.header}
                        </Typography>
                        {item.text && (
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: '0.9375rem',
                              lineHeight: 1.7,
                              color: 'text.primary',
                              pl: 1.5,
                              fontWeight: isPositiveText ? 'bold' : 'normal',
                            }}
                          >
                            <HighlightedLevelText text={item.text} issues={numberingIssues} section={section.title.replace(/:$/, '').trim()} />
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      item.text && (
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: '0.9375rem',
                            lineHeight: 1.7,
                            color: 'text.primary',
                            pl: 1.5,
                            fontWeight: isPositiveText ? 'bold' : 'normal',
                          }}
                        >
                          <HighlightedLevelText text={item.text} issues={numberingIssues} section={section.title.replace(/:$/, '').trim()} />
                        </Typography>
                      )
                    )}
                  </Box>
                );
              });
            })()}
          </Box>
        ) : (
          <Typography
            variant="body1"
            sx={{
              fontSize: '0.9375rem',
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
              color: 'text.primary',
            }}
          >
            {section.content
              ? <HighlightedLevelText text={section.content} issues={numberingIssues} section={section.title.replace(/:$/, '').trim()} />
              : <em style={{ color: '#9e9e9e' }}>No content</em>}
          </Typography>
        )
      )}

      {/* Impression workflow buttons */}
      {isImpression && canEdit && !isEditing && !isAnyEditing && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            variant="outlined"
            size="small"
            color="success"
            startIcon={<ThumbUpIcon />}
            onClick={onAcceptImpression}
            disabled={isSaving || status === 'review'}
            sx={{ textTransform: 'none' }}
          >
            Accept Impression
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            startIcon={<RefreshIcon />}
            onClick={onRegenerateImpression}
            disabled={isSaving || loading}
            sx={{ textTransform: 'none' }}
          >
            Regenerate
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => onManualEditImpression(section.content)}
            disabled={isSaving}
            sx={{ textTransform: 'none' }}
          >
            Edit Impression
          </Button>
        </Box>
      )}
    </Paper>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Main Page Component
// ═══════════════════════════════════════════════════════════════════

const ReportEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const { report, loading, error, finalizationIssues, isEditing, isSaving, isFinalized, config: reportConfig } = useAppSelector(
    (state) => state.report,
  );
  const isEditable = useAppSelector(selectIsReportEditable);
  const hardLockEnabled = reportConfig?.hardLockEnabled ?? false;
  const { boldPositives } = useAppSelector(selectReportingPrefs);
  const user = useAppSelector((state) => state.auth.user);
  const radiologistName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username : undefined;

  // Local state
  const [editedSections, setEditedSections] = useState<Record<string, string>>({});
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showError, setShowError] = useState(false);
  const [contextPanelOpen, setContextPanelOpen] = useState(true);
  const [pageLoading, setPageLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [patientContext, setPatientContext] = useState<PatientContext | undefined>(undefined);
  const [patientHistory, setPatientHistory] = useState<PatientHistorySummaryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [addendumOpen, setAddendumOpen] = useState(false);
  const [addendumText, setAddendumText] = useState('');
  const [addendumReason, setAddendumReason] = useState<'correction' | 'supplement' | 'clarification'>('supplement');
  const [addendumLoading, setAddendumLoading] = useState(false);
  const [addendumError, setAddendumError] = useState<string | null>(null);
  const [addendumSuccess, setAddendumSuccess] = useState<string | null>(null);
  const [doctorName, setDoctorName] = useState<string | null>(null);

  // Refs for section scroll-to
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  // Detect mode from URL
  const mode = searchParams.get('mode') || 'edit';
  const targetSessionId = searchParams.get('session');
  // Hard lock disabled → allow editing even when finalized (unless explicitly in view mode)
  const isViewOnly = mode === 'view' || (isFinalized && hardLockEnabled && mode !== 'edit');
  const canEdit = !isViewOnly && isEditable;

  // Load session + report on mount
  useEffect(() => {
    if (!targetSessionId) {
      setPageLoading(false);
      return;
    }

    let active = true;
    setPageLoading(true);

    (async () => {
      try {
        const detail = await transcriptionService.getSessionDetail(targetSessionId);
        if (!active) return;

        dispatch(loadSessionForAppend({
          sessionId: detail.session_id,
          rawText: detail.text,
          correctedText: detail.corrected_text || '',
          isUserEdited: detail.is_user_edited || false,
          classification: {
            category: detail.diagnosis_category || null,
            pathologyType: detail.pathology_type || null,
            confidence: detail.classification_confidence || null,
          },
        }));

        dispatch(setReport(detail.report));
      } catch (err) {
        console.error('Failed to load session for report editor:', err);
      } finally {
        if (active) setPageLoading(false);
      }
    })();

    return () => { active = false; };
  }, [targetSessionId, dispatch]);

  // Fetch patient context for exports
  useEffect(() => {
    if (!targetSessionId) return;
    let active = true;
    (async () => {
      try {
        const p = await patientService.getSessionPatient(targetSessionId);
        if (active && p) {
          setPatientContext({
            first_name: p.first_name,
            last_name: p.last_name,
            mrn: p.mrn,
            dob: p.dob,
            gender: p.gender,
          });
        }
      } catch {
        // No patient linked — leave undefined
      }
    })();
    return () => { active = false; };
  }, [targetSessionId]);

  // Fetch patient history for display after Impression
  useEffect(() => {
    if (!targetSessionId) {
      setPatientHistory([]);
      return;
    }

    let active = true;
    setHistoryLoading(true);

    (async () => {
      try {
        const p = await patientService.getSessionPatient(targetSessionId);
        if (!active || !p?.id) {
          setPatientHistory([]);
          setHistoryLoading(false);
          return;
        }
        const response = await patientService.getPatientHistorySummary(p.id, 5, report?.id);
        if (!active) return;
        setPatientHistory(response.history);
      } catch {
        if (!active) return;
        setPatientHistory([]);
      } finally {
        if (active) setHistoryLoading(false);
      }
    })();

    return () => { active = false; };
  }, [targetSessionId, report?.id]);

  // Fetch referring doctor name when report has doctor_id
  useEffect(() => {
    if (!report?.doctor_id) {
      setDoctorName(null);
      return;
    }
    const doctorId = report.doctor_id;
    let active = true;
    (async () => {
      try {
        const doctor = await doctorService.getDoctor(doctorId);
        if (active) setDoctorName(doctor.name);
      } catch {
        if (active) setDoctorName(null);
      }
    })();
    return () => { active = false; };
  }, [report?.doctor_id]);

  // Reset edit state when report changes
  useEffect(() => {
    setEditedSections({});
    setEditingSection(null);
    setConfirmFinalize(false);
  }, [report?.id]);

  // Show error notification
  useEffect(() => {
    if (error) setShowError(true);
  }, [error]);

  // Handlers
  const handleCopy = async () => {
    if (!report) return;
    try {
      await copyReportToClipboard(report.report_text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = report.report_text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleStartEdit = useCallback(
    (sectionTitle: string, content: string) => {
      if (!canEdit) return;
      setEditingSection(sectionTitle);
      setEditedSections((prev) => ({ ...prev, [sectionTitle]: content }));
      dispatch(setEditing(true));
    },
    [canEdit, dispatch],
  );

  const handleCancelEdit = useCallback(() => {
    setEditingSection(null);
    setEditedSections({});
    dispatch(setEditing(false));
  }, [dispatch]);

  const handleSaveSection = useCallback(() => {
    if (!report || !editingSection) return;
    const sections: Record<string, string> = {};
    if (editedSections[editingSection] !== undefined) {
      sections[editingSection] = editedSections[editingSection];
    }
    dispatch(updateReport({ reportId: report.id, data: { sections } }));
    setEditingSection(null);
    setEditedSections({});
  }, [report, editingSection, editedSections, dispatch]);

  const handleEditChange = useCallback((title: string, value: string) => {
    setEditedSections((prev) => ({ ...prev, [title]: value }));
  }, []);

  const handleAcceptImpression = useCallback(() => {
    if (!report) return;
    dispatch(updateReport({ reportId: report.id, data: { sections: {}, status: 'review' } }));
  }, [report, dispatch]);

  const handleRegenerateImpression = useCallback(() => {
    if (!report || !canEdit) return;
    dispatch(regenerateReport({
      sessionId: report.session_id,
      cptCode: report.cpt_code ?? undefined,
      patientContext: patientContext && patientContext.mrn && patientContext.dob
        ? {
            mrn: patientContext.mrn,
            dob: patientContext.dob,
            first_name: patientContext.first_name,
            last_name: patientContext.last_name,
            gender: patientContext.gender,
          }
        : undefined,
    }));
  }, [report, patientContext, dispatch, canEdit]);

  const handleManualEditImpression = useCallback(
    (content: string) => {
      handleStartEdit('IMPRESSION', content);
    },
    [handleStartEdit],
  );

  const handleFinalize = useCallback(async () => {
    if (!report) return;
    if (!confirmFinalize) {
      setConfirmFinalize(true);
      return;
    }
    const result = await dispatch(finalizeReport(report.id));
    if (finalizeReport.rejected.match(result)) {
      const payload = result.payload as { issues?: VertebralNumberingIssue[] } | undefined;
      if (payload?.issues?.length) setContextPanelOpen(true);
    }
    setConfirmFinalize(false);
  }, [report, confirmFinalize, dispatch]);

  const handleExportPDF = useCallback(async () => {
    console.debug('PDF_EXPORT_CLICKED_EDITOR | report_id=%d status=%s', report?.id, report?.status);
    
    if (!report || report.status !== 'final') {
      console.warn('EXPORT_BLOCKED_NOT_FINAL | report_id=%d status=%s', report?.id, report?.status);
      setExportError('Only finalized reports can be exported.');
      return;
    }
    
    console.debug('EXPORT_HANDLER_TRIGGERED_EDITOR | report_id=%d', report.id);
    setExporting(true);
    
    try {
      await exportReportPDF(report, { radiologistName, patientContext, exportTheme: reportConfig?.exportTheme });
      console.log('PDF_EXPORT_COMPLETED | report_id=%d', report.id);
    } catch (err: unknown) {
      console.error('EXPORT_HANDLER_ERROR_EDITOR | error=%o', err);
      setExportError((err as Error)?.message || 'PDF export failed');
    } finally {
      setExporting(false);
    }
  }, [report, radiologistName, patientContext, reportConfig?.exportTheme]);

  const handlePrint = useCallback(() => {
    if (!report || report.status !== 'final') {
      setExportError('Only finalized reports can be printed.');
      return;
    }
    try {
      printReport(report, { radiologistName, patientContext, exportTheme: reportConfig?.exportTheme });
    } catch (err: unknown) {
      setExportError((err as Error)?.message || 'Print failed');
    }
  }, [report, radiologistName, patientContext, reportConfig?.exportTheme]);

  const handleDownload = useCallback(() => {
    if (!report || report.status !== 'final') {
      setExportError('Only finalized reports can be downloaded.');
      return;
    }
    try {
      downloadReportText(report);
    } catch (err: unknown) {
      setExportError((err as Error)?.message || 'Download failed');
    }
  }, [report]);

  const handleExportHTML = useCallback(async () => {
    if (!report || report.status !== 'final') {
      setExportError('Only finalized reports can be exported.');
      return;
    }
    setExporting(true);
    try {
      await reportService.exportReportHTML(report.id, report.session_id);
      console.log('HTML_EXPORT_COMPLETED | report_id=%d', report.id);
    } catch (err: unknown) {
      console.error('HTML_EXPORT_ERROR | error=%o', err);
      setExportError((err as Error)?.message || 'HTML export failed');
    } finally {
      setExporting(false);
    }
  }, [report]);

  const handleExportDOCX = useCallback(async () => {
    if (!report || report.status !== 'final') {
      setExportError('Only finalized reports can be exported.');
      return;
    }
    setExporting(true);
    try {
      await reportService.exportReportDOCX(report.id, report.session_id);
      console.log('DOCX_EXPORT_COMPLETED | report_id=%d', report.id);
    } catch (err: unknown) {
      console.error('DOCX_EXPORT_ERROR | error=%o', err);
      setExportError((err as Error)?.message || 'Word export failed');
    } finally {
      setExporting(false);
    }
  }, [report]);

  const handleOpenAddendum = useCallback(() => {
    setAddendumText('');
    setAddendumError(null);
    setAddendumSuccess(null);
    setAddendumOpen(true);
  }, []);

  const handleCloseAddendum = useCallback(() => {
    setAddendumOpen(false);
    setAddendumText('');
    setAddendumError(null);
  }, []);

  const handleGenerateAddendum = useCallback(async () => {
    if (!report || !addendumText.trim()) return;
    setAddendumLoading(true);
    setAddendumError(null);
    setAddendumSuccess(null);
    try {
      const result = await addendumService.generateAddendum({
        report_id: report.id,
        dictated_text: addendumText.trim(),
        reason: addendumReason,
        session_id: report.session_id || targetSessionId || undefined,
      });
      setAddendumSuccess('Addendum created successfully.');
      setAddendumText('');
      console.log('ADDENDUM_GENERATED | report_id=%d addendum_id=%d', result.report_id, result.addendum_id);
    } catch (err: unknown) {
      console.error('ADDENDUM_GENERATE_ERROR | error=%o', err);
      setAddendumError((err as Error)?.message || 'Failed to generate addendum');
    } finally {
      setAddendumLoading(false);
    }
  }, [report, addendumText, addendumReason, targetSessionId]);

  const handleScrollToSection = (title: string) => {
    const el = sectionRefs.current[title];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleBack = () => {
    if (targetSessionId) {
      navigate(`/transcribe?session=${targetSessionId}`);
    } else {
      navigate(-1);
    }
  };

  // ── Loading skeleton ──
  if (pageLoading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 2 }} />
        <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
          <Skeleton variant="rectangular" width={200} sx={{ borderRadius: 2 }} />
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
          </Box>
        </Box>
      </Box>
    );
  }

  // ── No report ──
  if (!report && !loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
        <DescriptionIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
        <Typography variant="h6" color="text.secondary">
          No report available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Generate a report from the dictation page first.
        </Typography>
        <Button variant="contained" size="small" startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Dictation
        </Button>
      </Box>
    );
  }

  // ── Generating report ──
  if (loading && !report) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Generating radiology report...
        </Typography>
      </Box>
    );
  }

  if (!report) return null;

  const sections = parseReportSections(report.report_text);
  const status = report.status || 'draft';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>

      {/* ═══════════════════════════════════════════════════════════
          STICKY CLINICAL TOOLBAR
          ═══════════════════════════════════════════════════════════ */}
      <Paper
        elevation={2}
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
          borderRadius: 2,
          mb: 1.5,
          zIndex: 10,
        }}
      >
        {/* Back button */}
        <Tooltip title="Back to dictation">
          <IconButton size="small" onClick={handleBack} sx={{ mr: 0.5 }}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        {/* Title + status */}
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', mr: 1 }}>
          Radiology Report
        </Typography>
        <ReportStatusBadge
          status={status as 'draft' | 'review' | 'final' | null}
          size="small"
          showIcon
          hardLockEnabled={hardLockEnabled}
        />

        {/* CPT Code chip */}
        {report.cpt_code ? (
          <Chip
            label={`${report.cpt_code} — ${report.cpt_exam_name || ''}`}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontSize: '0.75rem' }}
          />
        ) : (
          <Chip
            label="No CPT Code"
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.75rem' }}
          />
        )}

        {/* Referring Doctor badge */}
        {doctorName && (
          <Tooltip title={`Referring doctor: ${doctorName}`}>
            <Chip
              icon={<MedicalServicesIcon sx={{ fontSize: 16 }} />}
              label={doctorName}
              size="small"
              variant="outlined"
              color="secondary"
              sx={{ fontSize: '0.75rem' }}
            />
          </Tooltip>
        )}

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Toolbar actions */}
        <Button
          variant="outlined"
          size="small"
          startIcon={copied ? <CheckIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
          onClick={handleCopy}
          color={copied ? 'success' : 'inherit'}
          sx={{ fontSize: '0.8rem', borderRadius: 2, textTransform: 'none', minWidth: 80 }}
        >
          {copied ? 'Copied!' : 'Copy'}
        </Button>

        {/* Export actions — visible ONLY for finalized reports */}
        {isFinalized && (
          <>
            <Tooltip title="Export as PDF">
              <IconButton aria-label="Export as PDF" size="small" onClick={handleExportPDF} disabled={exporting}>
                <PictureAsPdfIcon fontSize="small" color="error" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download HTML">
              <IconButton size="small" onClick={handleExportHTML} disabled={exporting}>
                <CodeIcon fontSize="small" color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download Word">
              <IconButton size="small" onClick={handleExportDOCX} disabled={exporting}>
                <ArticleIcon fontSize="small" color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print report">
              <IconButton size="small" onClick={handlePrint}>
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download as text">
              <IconButton size="small" onClick={handleDownload}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}

        {/* Voice-driven addendum — only on finalized reports */}
        {isFinalized && (
          <Tooltip title="Add dictated addendum">
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              startIcon={<MicIcon sx={{ fontSize: 18 }} />}
              onClick={handleOpenAddendum}
              sx={{ fontSize: '0.8rem', borderRadius: 2, textTransform: 'none', minWidth: 80, ml: 0.5 }}
            >
              Add Addendum
            </Button>
          </Tooltip>
        )}

        {/* Context panel toggle */}
        <Tooltip title={contextPanelOpen ? 'Hide context panel' : 'Show context panel'}>
          <IconButton
            size="small"
            onClick={() => setContextPanelOpen(!contextPanelOpen)}
            color={contextPanelOpen ? 'primary' : 'default'}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Finalize — shown only when report is not yet finalized and hard lock is disabled */}
        {canEdit && !hardLockEnabled && !isFinalized && !confirmFinalize && (
          <Button
            variant="contained"
            size="small"
            color="success"
            startIcon={<LockIcon />}
            onClick={handleFinalize}
            disabled={isSaving}
          >
            Finalize Report
          </Button>
        )}

        {canEdit && !hardLockEnabled && !isFinalized && confirmFinalize && (
          <>
            <Alert severity="warning" sx={{ py: 0, px: 1, fontSize: '0.8rem' }}>
              Finalize is irreversible
            </Alert>
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={handleFinalize}
              disabled={isSaving}
            >
              Confirm
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setConfirmFinalize(false)}
            >
              Cancel
            </Button>
          </>
        )}

        {isViewOnly && (
          <Chip
            icon={<LockIcon sx={{ fontSize: 14 }} />}
            label="View Only"
            size="small"
            color="default"
            variant="outlined"
          />
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>
          <Typography variant="body2" fontWeight={600}>{error}</Typography>
          {finalizationIssues.length > 0 && (
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              {finalizationIssues.flatMap((issue) => issue.affected_levels).filter((level, index, levels) => levels.indexOf(level) === index).map((level) => (
                <Chip key={level} label={level} size="small" color="error" variant="outlined" />
              ))}
            </Stack>
          )}
        </Alert>
      )}

      {/* Read-only alert for finalized reports */}
      {isViewOnly && (
        <Alert
          severity="info"
          icon={<LockIcon />}
          sx={{ mb: 1.5, borderRadius: 2, flexShrink: 0 }}
        >
          This report is <strong>finalized</strong> and cannot be edited.
          {report.finalized_at && (
            <> Finalized on {new Date(report.finalized_at).toLocaleString()}.</>
          )}
        </Alert>
      )}

      {/* ═══════════════════════════════════════════════════════════
          MAIN WORKSPACE: Section Navigator + Editing Canvas
          ═══════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          gap: 1.5,
          overflow: 'hidden',
        }}
      >

        {/* ─── LEFT: Section Navigator ─── */}
        <Paper
          elevation={0}
          sx={{
            width: 200,
            flexShrink: 0,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'auto',
            display: { xs: 'none', md: 'block' },
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            textTransform="uppercase"
            letterSpacing={0.5}
            sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block' }}
          >
            Sections
          </Typography>
          <List dense disablePadding>
            {sections.map((section) => (
              <ListItemButton
                key={section.title}
                onClick={() => handleScrollToSection(section.title)}
                selected={editingSection === section.title}
                sx={{
                  py: 0.75,
                  px: 2,
                  '&.Mui-selected': {
                    borderLeft: '3px solid',
                    borderColor: 'primary.main',
                    backgroundColor: 'action.selected',
                  },
                }}
              >
                <ListItemText
                  primary={section.title}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: editingSection === section.title ? 600 : 400,
                    fontSize: '0.8rem',
                    sx: { textTransform: 'capitalize' },
                  }}
                />
              </ListItemButton>
            ))}
          </List>

          {/* Metadata at bottom of navigator */}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {sections.length} section{sections.length !== 1 ? 's' : ''}
            </Typography>
            {report.last_edited_at && (
              <Typography variant="caption" color="text.secondary" display="block">
                Edited: {new Date(report.last_edited_at).toLocaleDateString()}
              </Typography>
            )}
            {report.created_at && (
              <Typography variant="caption" color="text.secondary" display="block">
                Created: {new Date(report.created_at).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        </Paper>

        {/* ─── CENTER: Editing Canvas ─── */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
            px: { xs: 0, md: 2 },
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 },
          }}
        >
          <Box
            sx={{
              maxWidth: 900,
              mx: 'auto',
              py: 1,
            }}
          >
            {sections.map((section) => {
              const isImpressionSection = section.title.replace(/:$/, '').trim().toUpperCase() === 'IMPRESSION';
              const isEditingThis = editingSection === section.title;
              const editValue = editedSections[section.title] ?? section.content;

              return (
                <React.Fragment key={section.title}>
                  <SectionCard
                    section={section}
                    canEdit={canEdit}
                    isEditing={isEditingThis}
                    isAnyEditing={isEditing}
                    isSaving={isSaving}
                    isImpression={isImpressionSection}
                    status={status}
                    loading={loading}
                    boldPositives={boldPositives}
                    editValue={editValue}
                    numberingIssues={report.vertebral_numbering_data?.issues || []}
                    onStartEdit={handleStartEdit}
                    onCancelEdit={handleCancelEdit}
                    onSaveSection={handleSaveSection}
                    onEditChange={handleEditChange}
                    onAcceptImpression={handleAcceptImpression}
                    onRegenerateImpression={handleRegenerateImpression}
                    onManualEditImpression={handleManualEditImpression}
                    sectionRef={(el) => { sectionRefs.current[section.title] = el; }}
                  />

                  {/* Patient History Summary — after Impression */}
                  {isImpressionSection && patientHistory.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 2, md: 3 },
                        mb: 2,
                        border: '1px solid',
                        borderColor: 'info.light',
                        borderRadius: 2,
                        bgcolor: 'grey.50',
                        borderLeft: 4,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <HistoryIcon sx={{ fontSize: 18, color: 'info.main' }} />
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          color="info.dark"
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.875rem' }}
                        >
                          Patient History Summary
                        </Typography>
                        <Chip
                          label={`${patientHistory.length} prior ${patientHistory.length === 1 ? 'report' : 'reports'}`}
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 22, ml: 'auto' }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {patientHistory.map((entry, histIdx) => (
                          <Box
                            key={histIdx}
                            sx={{
                              p: 1.5,
                              backgroundColor: 'white',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}>
                              {entry.date && (
                                <Chip
                                  icon={<CalendarTodayIcon sx={{ fontSize: 12 }} />}
                                  label={entry.date}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                                />
                              )}
                              {entry.modality && (
                                <Chip
                                  label={entry.modality}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                                />
                              )}
                              {entry.body_part && (
                                <Chip
                                  icon={<LocalHospitalIcon sx={{ fontSize: 12 }} />}
                                  label={entry.body_part}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 22, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}
                            >
                              {entry.summary}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  )}

                  {isImpressionSection && historyLoading && (
                    <Paper
                      elevation={0}
                      sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Loading patient history...
                      </Typography>
                    </Paper>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        </Box>

        {/* ─── RIGHT: Context / AI Panel (collapsible) ─── */}
        <Collapse in={contextPanelOpen} orientation="horizontal" unmountOnExit>
          <Paper
            elevation={0}
            sx={{
              width: 320,
              flexShrink: 0,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'auto',
              p: 2,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              textTransform="uppercase"
              letterSpacing={0.5}
              sx={{ mb: 1.5, display: 'block' }}
            >
              Report Context
            </Typography>

            {/* Patient Information */}
            {targetSessionId && (
              <PatientInfoDisplay sessionId={targetSessionId} />
            )}

            {/* Metadata */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>CPT Code:</strong> {report.cpt_code
                  ? `${report.cpt_code} — ${report.cpt_exam_name || ''}`
                  : '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Created:</strong> {new Date(report.created_at).toLocaleString()}
              </Typography>
              {report.finalized_at && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Finalized:</strong> {new Date(report.finalized_at).toLocaleString()}
                </Typography>
              )}
            </Box>

            {report.vertebral_numbering_data && (
              <>
                <Divider sx={{ my: 2 }} />
                <VertebralNumberingPanel
                  report={report}
                  editable={canEdit}
                  onUpdated={(updated) => dispatch(setReport(updated))}
                />
              </>
            )}

          </Paper>
        </Collapse>
      </Box>

      {/* Voice-driven Addendum Dialog */}
      <Dialog
        open={addendumOpen}
        onClose={handleCloseAddendum}
        maxWidth="sm"
        fullWidth
        aria-labelledby="addendum-dialog-title"
      >
        <DialogTitle id="addendum-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MicIcon color="secondary" />
            <Typography variant="h6" component="span">
              Dictated Addendum
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Speak or type the addendum text below. The AI will format it against
            the finalized report and persist it as a structured addendum.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="addendum-reason-label">Addendum reason</InputLabel>
            <Select
              labelId="addendum-reason-label"
              value={addendumReason}
              label="Addendum reason"
              onChange={(e) => setAddendumReason(e.target.value as 'correction' | 'supplement' | 'clarification')}
            >
              <MenuItem value="supplement">Supplement</MenuItem>
              <MenuItem value="correction">Correction</MenuItem>
              <MenuItem value="clarification">Clarification</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            minRows={4}
            maxRows={10}
            label="Dictated addendum"
            placeholder="Example: I need to add that the patient has a small left lower lobe nodule, unchanged from prior..."
            value={addendumText}
            onChange={(e) => setAddendumText(e.target.value)}
            disabled={addendumLoading}
            sx={{ mb: 2 }}
          />
          {addendumError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {addendumError}
            </Alert>
          )}
          {addendumSuccess && (
            <Alert severity="success" sx={{ mb: 1 }}>
              {addendumSuccess}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddendum} disabled={addendumLoading}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateAddendum}
            disabled={!addendumText.trim() || addendumLoading}
            startIcon={addendumLoading ? <CircularProgress size={16} /> : <AddCommentIcon />}
          >
            {addendumLoading ? 'Generating…' : 'Generate Addendum'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export error Snackbar */}
      <Snackbar
        open={!!exportError}
        autoHideDuration={5000}
        onClose={() => setExportError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setExportError(null)} variant="filled">
          {exportError}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showError && !!error}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowError(false)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportEditorPage;
